/**
 * API client for PillPoint backend.
 * Replaces the static JSON fetch previously in pillpointDataLoader.js.
 *
 * Env var: REACT_APP_API_URL (set in .env at project root)
 * Falls back to /api for same-origin proxy (e.g. via package.json "proxy").
 */

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

const get = async (path, params = {}) => {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
  });

  const res = await fetch(url.toString());
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw Object.assign(new Error(err.message || 'API error'), { status: res.status });
  }
  return res.json();
};

const post = async (path, body) => {
  const url = `${BASE_URL}${path}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw Object.assign(new Error(err.message || 'API error'), { status: res.status });
  }
  return res.json();
};

// ── Products ─────────────────────────────────────────

export const fetchProducts = (params = {}) => get('/products', params);

export const fetchProductBySlug = (slug) => get(`/products/${encodeURIComponent(slug)}`);

export const fetchProductById = (productId) => get(`/products/id/${encodeURIComponent(productId)}`);

// ── Categories ────────────────────────────────────────

export const fetchCategories = () => get('/categories');

export const fetchTopCategories = (limit = 6) => get('/categories/top', { limit });

export const fetchCategoryProducts = (slug, params = {}) =>
  get(`/categories/${encodeURIComponent(slug)}/products`, params);

// ── Blog ─────────────────────────────────────────────

export const fetchBlogPosts = (params = {}) => get('/blog', params);

export const fetchBlogPostBySlug = (slug) => get(`/blog/${encodeURIComponent(slug)}`);

export const fetchBlogCategories = () => get('/blog/categories');

// ── Orders ───────────────────────────────────────────

export const createOrder = (orderData) => post('/orders', orderData);

export const trackOrder = (orderId, email) => get('/orders/track', { orderId, email });
